import { compareAsc, getMonth, getYear } from 'date-fns';
import { IManagedFarmTask, managedFarmTaskStatus } from '../../interfaces/ManagedFarm.interfaces';
import { v4 as uuidv4 } from 'uuid';
import {
  getTaskListFromIndexedDB,
  saveTaskListToIndexedDB,
  addTaskToIndexedDB,
  updateTaskInIndexedDB,
  deleteTaskFromIndexedDB,
  migrateFromLocalStorage,
} from './managedFarmTaskIndexedDB.util';

// 마이그레이션 상태 관리
let migrationCompleted = false;

// 마이그레이션 보장 함수
const ensureMigration = async (): Promise<void> => {
  if (!migrationCompleted) {
    await migrateFromLocalStorage();
    migrationCompleted = true;
  }
};

export const createNewTask = (taskInfo: Omit<IManagedFarmTask, 'id'>): IManagedFarmTask => {
  const uuid = uuidv4();

  const newTask: IManagedFarmTask = {
    ...taskInfo,
    id: uuid,
  };

  return newTask;
};

export const getTaskById = async (taskId?: string): Promise<IManagedFarmTask | null> => {
  await ensureMigration();

  if (!taskId) {
    return null;
  }

  const savedTaskList = await getTaskListFromIndexedDB();
  const task = savedTaskList.find((task) => task.id === taskId);

  if (!task) {
    return null;
  }

  return task;
};

export const getTaskListFromStorage = async (): Promise<IManagedFarmTask[]> => {
  await ensureMigration();
  return getTaskListFromIndexedDB();
};

export const getTaskListByDate = async (date: Date): Promise<IManagedFarmTask[]> => {
  await ensureMigration();

  const savedTaskList = await getTaskListFromIndexedDB();

  const filteredTaskList = savedTaskList.filter((task) => {
    if (!task.startDate || !task.endDate) {
      return false;
    }

    const todayYear = getYear(date);
    const todayMonth = getMonth(date);
    const startYear = getYear(task.startDate);
    const startMonth = getMonth(task.startDate);
    const endYear = getYear(task.endDate);
    const endMonth = getMonth(task.endDate);

    const isWithinMonthRange =
      (todayYear > startYear || (todayYear === startYear && todayMonth >= startMonth)) &&
      (todayYear < endYear || (todayYear === endYear && todayMonth <= endMonth));

    if (isWithinMonthRange) {
      return true;
    }

    return false;
  });

  const sortedFilteredTaskList = filteredTaskList.sort((a, b) => {
    if (!a.startDate || !b.startDate) {
      return 0;
    }

    // 1순위: startDate 오름차순 비교
    const startDateComparison = compareAsc(a.startDate, b.startDate);
    if (startDateComparison !== 0) {
      return startDateComparison;
    }

    // 2순위: endDate 오름차순 비교 (startDate가 같은 경우)
    if (!a.endDate || !b.endDate) {
      return 0;
    }
    return compareAsc(a.endDate, b.endDate);
  });

  return sortedFilteredTaskList;
};

export const updateTaskStatusById = async (taskId: string, newStatus: managedFarmTaskStatus): Promise<void> => {
  await ensureMigration();

  const savedTaskList = await getTaskListFromIndexedDB();

  const taskToUpdate = savedTaskList.find((task) => task.id === taskId);
  if (taskToUpdate) {
    const updatedTask = { ...taskToUpdate, status: newStatus };
    await updateTaskInIndexedDB(updatedTask);
  }
};

export const addNewTaskToList = (list: IManagedFarmTask[], newTask: IManagedFarmTask): IManagedFarmTask[] => {
  const newTaskList: IManagedFarmTask[] = [...list, newTask];
  return newTaskList;
};

export const saveTaskListToStorage = async (newTaskList: IManagedFarmTask[]): Promise<void> => {
  await ensureMigration();
  await saveTaskListToIndexedDB(newTaskList);
};

export const deleteTaskById = async (taskId: string): Promise<void> => {
  await ensureMigration();
  await deleteTaskFromIndexedDB(taskId);
};

// 새로운 태스크 추가 (더 효율적)
export const addNewTaskToStorage = async (newTask: IManagedFarmTask): Promise<void> => {
  await ensureMigration();
  await addTaskToIndexedDB(newTask);
};
